import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Search,
  RefreshCw,
  Users,
  UserPlus,
  MoreHorizontal,
  Mail,
  Phone,
  Building2,
  Shield,
  ShieldCheck,
  Briefcase,
  UserCog,
  Eye,
  FileText,
  Loader2,
  Copy,
  X,
  CheckCircle2,
  XCircle,
  Clock,
  Edit3,
  Trash2,
  AlertTriangle,
  Filter
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role configuration
const ROLE_CONFIG = {
  SUPER_ADMIN: { label: 'Super Admin', color: 'bg-red-100 text-red-700 border-red-200', icon: ShieldCheck },
  CONSULTANT: { label: 'Consultant', color: 'bg-teal-100 text-teal-700 border-teal-200', icon: Briefcase },
  CONTRACTOR_ADMIN: { label: 'Contractor Admin', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Building2 },
  BID_MANAGER: { label: 'Bid Manager', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: UserCog },
  REVIEWER: { label: 'Reviewer', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Eye },
  VIEWER: { label: 'Viewer', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: Eye },
};

export default function UserManagement() {
  const { authToken } = useAuth();
  
  // Data state
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_role: {}, active_30d: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // Quick View state
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  
  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Deactivate modal
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);

  // Purge modal
  const [isPurgeOpen, setIsPurgeOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!authToken) return;
    setIsLoading(true);
    
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (roleFilter !== 'all') params.append('role', roleFilter);
      
      const [usersRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/users?${params}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }),
        fetch(`${API_URL}/api/admin/users/stats`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        })
      ]);
      
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data);
      }
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoading(false);
    }
  }, [authToken, searchQuery, roleFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle row click to open Quick View
  const handleRowClick = (user) => {
    setSelectedUser(user);
  };

  // Close Quick View
  const closeQuickView = () => {
    setSelectedUser(null);
  };

  // Open edit modal
  const openEditModal = () => {
    if (selectedUser) {
      setEditForm({
        first_name: selectedUser.first_name || '',
        last_name: selectedUser.last_name || '',
        phone: selectedUser.phone || '',
        title: selectedUser.title || '',
        role: selectedUser.role || 'VIEWER',
      });
      setIsEditModalOpen(true);
    }
  };

  // Save edit
  const handleSaveEdit = async () => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(editForm)
      });
      
      if (response.ok) {
        setIsEditModalOpen(false);
        fetchData();
        // Update selected user
        setSelectedUser(prev => ({ ...prev, ...editForm }));
      } else {
        const err = await response.json();
        alert(`Error: ${err.detail}`);
      }
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Deactivate user
  const handleDeactivateUser = async () => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}/deactivate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        setIsDeactivateOpen(false);
        closeQuickView();
        fetchData();
      } else {
        const err = await response.json();
        alert(`Error: ${err.detail}`);
      }
    } catch (error) {
      console.error('Error deactivating user:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Purge user (permanent hard-delete)
  const handlePurgeUser = async () => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        setIsPurgeOpen(false);
        closeQuickView();
        fetchData();
      } else {
        const err = await response.json();
        alert(`Error: ${err.detail}`);
      }
    } catch (error) {
      console.error('Error purging user:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Reactivate user
  const handleReactivateUser = async () => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}/reactivate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        fetchData();
        setSelectedUser(prev => ({ ...prev, is_active: true }));
      } else {
        const err = await response.json();
        alert(`Error: ${err.detail}`);
      }
    } catch (error) {
      console.error('Error reactivating user:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Role filter tabs
  const roleTabs = [
    { key: 'all', label: 'All Users', count: stats.total },
    { key: 'CONTRACTOR_ADMIN', label: 'Contractors', count: stats.by_role?.CONTRACTOR_ADMIN || 0 },
    { key: 'BID_MANAGER', label: 'Bid Managers', count: stats.by_role?.BID_MANAGER || 0 },
    { key: 'CONSULTANT', label: 'Consultants', count: stats.by_role?.CONSULTANT || 0 },
    { key: 'REVIEWER', label: 'Reviewers', count: stats.by_role?.REVIEWER || 0 },
  ];

  const statCards = [
    { label: 'Total Users', value: stats.total, icon: Users, color: 'text-blue-600', bgColor: 'bg-blue-50' },
    { label: 'Active (30d)', value: stats.active_30d, icon: CheckCircle2, color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
    { label: 'Contractors', value: stats.by_role?.CONTRACTOR_ADMIN || 0, icon: Building2, color: 'text-purple-600', bgColor: 'bg-purple-50' },
    { label: 'Consultants', value: stats.by_role?.CONSULTANT || 0, icon: Briefcase, color: 'text-teal-600', bgColor: 'bg-teal-50' },
  ];

  return (
    <div data-testid="user-management" className="flex">
      {/* Main Content */}
      <div className={cn("flex-1 transition-all duration-300", selectedUser ? "mr-[420px]" : "")}>
        <PageHeader 
          title="User Management"
          description="Manage all users across the platform - contractors, consultants, and team members"
          action={
            <Button
              variant="outline"
              onClick={fetchData}
              disabled={isLoading}
              className="border-slate-200"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          }
        />
        
        <div className="p-8 space-y-6">
          {/* Stats cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {statCards.map((stat, index) => (
              <Card key={stat.label || `stat-${index}`} className="border-slate-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">{stat.label}</p>
                      <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {stat.value}
                      </p>
                    </div>
                    <div className={`p-3 ${stat.bgColor} rounded-lg`}>
                      <stat.icon className={`w-5 h-5 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Role Filter Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <Filter className="w-4 h-4 text-slate-400 flex-shrink-0" />
            {roleTabs.map((tab) => (
              <Button
                key={tab.key}
                variant={roleFilter === tab.key ? "default" : "outline"}
                size="sm"
                onClick={() => setRoleFilter(tab.key)}
                className={cn(
                  "flex-shrink-0",
                  roleFilter === tab.key 
                    ? "bg-blue-700 hover:bg-blue-800 text-white" 
                    : "border-slate-200"
                )}
              >
                {tab.label}
                <Badge variant="secondary" className="ml-2 bg-white/20">
                  {tab.count}
                </Badge>
              </Button>
            ))}
          </div>

          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Search by name, email, or organization..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-slate-200"
              data-testid="user-search"
            />
          </div>

          {/* User list */}
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              {/* Table header */}
              <div className="grid grid-cols-7 gap-4 px-6 py-3 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wider">
                <div className="col-span-2">User</div>
                <div>Email</div>
                <div>Organization</div>
                <div>Role</div>
                <div>Status</div>
                <div>Last Login</div>
              </div>
              
              {isLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : users.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                    <Users className="w-8 h-8 text-slate-400" />
                  </div>
                  <p className="text-slate-500 text-sm mb-1">
                    {searchQuery || roleFilter !== 'all' ? 'No users match your filters' : 'No users found'}
                  </p>
                </div>
              ) : (
                users.map((user) => {
                  const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.VIEWER;
                  const RoleIcon = roleConfig.icon;
                  
                  return (
                    <div 
                      key={user.id} 
                      className={cn(
                        "grid grid-cols-7 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer",
                        selectedUser?.id === user.id && "bg-blue-50 hover:bg-blue-50",
                        !user.is_active && "opacity-60"
                      )}
                      onClick={() => handleRowClick(user)}
                      data-testid={`user-row-${user.id}`}
                    >
                      {/* User */}
                      <div className="col-span-2 flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center",
                          user.is_active ? "bg-blue-50" : "bg-slate-100"
                        )}>
                          <RoleIcon className={cn(
                            "w-5 h-5",
                            user.is_active ? "text-blue-700" : "text-slate-400"
                          )} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {user.first_name} {user.last_name}
                          </p>
                          {user.title && (
                            <p className="text-xs text-slate-500">{user.title}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Email */}
                      <div className="flex items-center">
                        <span className="text-sm text-slate-700 truncate" title={user.email}>
                          {user.email}
                        </span>
                      </div>
                      
                      {/* Organization */}
                      <div className="flex items-center">
                        <span className="text-sm text-slate-600 truncate" title={user.organization_name}>
                          {user.organization_name || '-'}
                        </span>
                      </div>
                      
                      {/* Role */}
                      <div className="flex items-center">
                        <Badge className={cn("border text-xs", roleConfig.color)}>
                          {roleConfig.label}
                        </Badge>
                      </div>
                      
                      {/* Status */}
                      <div className="flex items-center">
                        <Badge className={user.is_active 
                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' 
                          : 'bg-red-100 text-red-700 hover:bg-red-100'
                        }>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      
                      {/* Last Login */}
                      <div className="flex items-center text-sm text-slate-500">
                        {user.last_login_at ? (
                          <span title={formatDate(user.last_login_at)}>
                            {new Date(user.last_login_at).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-slate-400">Never</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* QUICK VIEW PANEL */}
      {selectedUser && (
        <div className="fixed right-0 top-0 h-full w-[420px] bg-slate-50 border-l border-slate-200 shadow-xl z-40 overflow-y-auto" data-testid="user-quick-view">
          {/* Panel Header */}
          <div className={cn(
            "px-6 py-4 text-white",
            selectedUser.is_active ? "bg-blue-700" : "bg-slate-600"
          )}>
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-semibold uppercase tracking-wider opacity-80">User Profile</p>
              <button 
                onClick={closeQuickView}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                {(() => {
                  const RoleIcon = ROLE_CONFIG[selectedUser.role]?.icon || Users;
                  return <RoleIcon className="w-6 h-6" />;
                })()}
              </div>
              <div>
                <h3 className="font-semibold">{selectedUser.first_name} {selectedUser.last_name}</h3>
                <p className="text-sm opacity-80">{selectedUser.title || ROLE_CONFIG[selectedUser.role]?.label}</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Status & Role */}
            <div className="flex items-center justify-between">
              <Badge className={cn("border", ROLE_CONFIG[selectedUser.role]?.color)}>
                {ROLE_CONFIG[selectedUser.role]?.label || selectedUser.role}
              </Badge>
              <Badge className={selectedUser.is_active 
                ? 'bg-emerald-100 text-emerald-700' 
                : 'bg-red-100 text-red-700'
              }>
                {selectedUser.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-3">Contact Information</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-700">{selectedUser.email}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => copyToClipboard(selectedUser.email)}
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                {selectedUser.phone && (
                  <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-700">{selectedUser.phone}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Organization */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-3">Organization</h4>
              <div className="p-3 bg-white rounded-lg border border-slate-200">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <span className="font-medium text-slate-900">{selectedUser.organization_name || 'N/A'}</span>
                </div>
                {selectedUser.organization_id && (
                  <p className="text-xs text-slate-400 mt-1 font-mono">{selectedUser.organization_id}</p>
                )}
              </div>
            </div>

            {/* Timestamps */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-3">Activity</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                  <span className="text-slate-500">Last Login</span>
                  <span className="text-slate-700">{formatDate(selectedUser.last_login_at)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                  <span className="text-slate-500">Created</span>
                  <span className="text-slate-700">{formatDate(selectedUser.created_at)}</span>
                </div>
              </div>
            </div>

            {/* User ID */}
            <div className="text-xs text-slate-400">
              <p>User ID: {selectedUser.id}</p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 pt-4 border-t border-slate-200">
              <Button
                className="w-full bg-blue-700 hover:bg-blue-800 text-white"
                onClick={openEditModal}
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
              {selectedUser.is_active ? (
                <Button
                  variant="outline"
                  className="w-full border-amber-200 text-amber-700 hover:bg-amber-50"
                  onClick={() => setIsDeactivateOpen(true)}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Deactivate User
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="w-full border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                  onClick={handleReactivateUser}
                  disabled={isProcessing}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Reactivate User
                </Button>
              )}
              {selectedUser.role !== 'SUPER_ADMIN' && (
                <Button
                  variant="outline"
                  className="w-full border-red-300 text-red-700 hover:bg-red-50"
                  onClick={() => setIsPurgeOpen(true)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Purge User
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* EDIT PROFILE MODAL */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="edit-user-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-blue-600" />
              Edit User Profile
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input
                  value={editForm.first_name || ''}
                  onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input
                  value={editForm.last_name || ''}
                  onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={editForm.phone || ''}
                onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                placeholder="(555) 123-4567"
              />
            </div>

            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={editForm.title || ''}
                onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                placeholder="CEO, Manager, etc."
              />
            </div>

            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({...editForm, role: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CONTRACTOR_ADMIN">Contractor Admin</SelectItem>
                  <SelectItem value="BID_MANAGER">Bid Manager</SelectItem>
                  <SelectItem value="REVIEWER">Reviewer</SelectItem>
                  <SelectItem value="VIEWER">Viewer</SelectItem>
                  <SelectItem value="CONSULTANT">Consultant</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={isProcessing}
              className="bg-blue-700 hover:bg-blue-800 text-white"
            >
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PURGE USER MODAL */}
      <Dialog open={isPurgeOpen} onOpenChange={setIsPurgeOpen}>
        <DialogContent className="sm:max-w-md" data-testid="purge-user-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Permanently Purge User
            </DialogTitle>
            <DialogDescription>
              This will permanently delete{' '}
              <strong>{selectedUser?.first_name} {selectedUser?.last_name}</strong>{' '}
              ({selectedUser?.email}) and all associated data. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>All user records, team memberships, and session tokens will be permanently removed.</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPurgeOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handlePurgeUser}
              disabled={isProcessing}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-purge-user-btn"
            >
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Purge Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DEACTIVATE MODAL */}
      <Dialog open={isDeactivateOpen} onOpenChange={setIsDeactivateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="deactivate-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Deactivate User
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to deactivate {selectedUser?.first_name} {selectedUser?.last_name}? 
              They will not be able to log in until reactivated.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeactivateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDeactivateUser}
              disabled={isProcessing}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
